<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUpgradePreviewQuery } from '@/entities/payment'
import { HttpError } from '@/shared/api/errors'
import CButton from './CButton.vue'
import CDialog from './CDialog.vue'

interface Props {
  tariffId: number | null
  period: number | null
  tariffName: string
  isSubmitting?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  confirm: []
}>()

const model = defineModel<boolean>()
const { t } = useI18n()

const previewParams = computed(() => {
  if (!model.value || props.tariffId == null || props.period == null)
    return null
  return { tariff_id: props.tariffId, period: props.period }
})

const { data: preview, isLoading, error } = useUpgradePreviewQuery(previewParams)

const tariffPrice = computed(() => parseFloat(preview.value?.tariff_price ?? '0'))
const remainingCredit = computed(() =>
  parseFloat(preview.value?.remaining_credit ?? '0'),
)
const payingAmount = computed(() =>
  parseFloat(preview.value?.paying_amount ?? '0'),
)

// Edge case: when remaining credit exceeds the new tariff price the backend
// floors paying_amount to 1 ₽ and the surplus is lost (not refunded).
const isCreditPartiallyLost = computed(
  () => remainingCredit.value > tariffPrice.value && payingAmount.value <= 1,
)
const lostCredit = computed(() =>
  Math.max(remainingCredit.value - tariffPrice.value, 0),
)

const hasCredit = computed(() => remainingCredit.value > 0)

function formatPrice(value: number): string {
  // Non-breaking space between the number and the ₽ symbol so the
  // currency never wraps to its own line on narrow viewports.
  return `${Math.round(value).toLocaleString('ru-RU')}\u00A0₽`
}

const errorMessage = ref<string | null>(null)
watch(
  error,
  async (err) => {
    errorMessage.value = null
    if (!err) 
      return
    if (err instanceof HttpError) {
      const data = (err.errorData ?? (await err.parseError())) as unknown
      if (Array.isArray(data) && typeof data[0] === 'string') {
        errorMessage.value = data[0]
      }
      else if (
        data
        && typeof data === 'object'
        && 'detail' in (data as Record<string, unknown>)
      ) {
        errorMessage.value = String((data as Record<string, unknown>).detail)
      }
    }
    if (!errorMessage.value) 
      errorMessage.value = t('payment_creation_error')
  },
  { immediate: true },
)

function handleCancel() {
  model.value = false
}

function handleConfirm() {
  emit('confirm')
}
</script>

<template>
  <CDialog v-model="model" :title="$t('tariff_upgrade_confirm_title')">
    <template #header>
      <div class="flex items-center gap-2">
        <i class="icon-crown-minimalistic-outline text-2xl text-brand-violet" />
        <span class="text-lg font-semibold">
          {{ $t('tariff_upgrade_confirm_heading', { name: tariffName }) }}
        </span>
      </div>
    </template>

    <div class="flex flex-col gap-4 min-h-[120px]">
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-8 text-text-secondary text-sm"
      >
        {{ $t('loading') }}
      </div>

      <div
        v-else-if="errorMessage"
        class="text-sm text-red-600 bg-red-50 rounded-lg p-3"
      >
        {{ errorMessage }}
      </div>

      <div v-else-if="preview" class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-3 text-sm">
          <span class="text-text-regular">
            {{ $t('tariff_upgrade_price') }}
          </span>
          <span class="font-medium whitespace-nowrap">
            {{ formatPrice(tariffPrice) }}
          </span>
        </div>

        <div
          v-if="hasCredit"
          class="flex items-center justify-between gap-3 text-sm text-green-700"
        >
          <span>{{ $t('tariff_upgrade_remaining_credit') }}</span>
          <span class="font-medium whitespace-nowrap">
            −&nbsp;{{ formatPrice(remainingCredit) }}
          </span>
        </div>

        <div class="h-px bg-border-lighter my-1" />

        <div class="flex items-center justify-between gap-3">
          <span class="text-base font-semibold text-text-primary">
            {{ $t('tariff_upgrade_to_pay') }}
          </span>
          <span class="text-xl font-bold text-blue-light-3 whitespace-nowrap">
            {{ formatPrice(payingAmount) }}
          </span>
        </div>

        <div
          v-if="isCreditPartiallyLost"
          class="text-xs text-amber-700 bg-amber-50 rounded-lg p-3 leading-relaxed"
        >
          {{ $t('tariff_upgrade_credit_lost_warning', {
            amount: formatPrice(lostCredit),
          }) }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <CButton :disabled="isSubmitting" @click="handleCancel">
          {{ $t('cancel') }}
        </CButton>
        <CButton
          type="primary"
          :loading="isSubmitting"
          :disabled="isLoading || !!errorMessage"
          @click="handleConfirm"
        >
          {{ preview ? $t('tariff_upgrade_pay_cta', {
            amount: formatPrice(payingAmount),
          }) : $t('tariff_upgrade_pay_cta_plain') }}
        </CButton>
      </div>
    </template>
  </CDialog>
</template>
